<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";
import IconChevron from "../icons/iconChevron.vue";

const props = defineProps({
    label: {
        type: String,
        default: null
    },
    required: {
        type: Boolean,
        default: false
    },
    type: {
        type: String,
        required: true
    },
    content: null,
    value: null,
    selectedMediaBankEntryId: {
        type: Number,
        required: false,
        default: null
    }
})

const emit = defineEmits(['update:content'])

const waiting = ref(false)
const showUploader = ref(true)
const mediaBankEntryOptions = ref(null)
const value = ref(props.content)
const searchterm = ref('')
const showGallery = ref(false)
const currentPage = ref(1)
const currentGalleryPage = ref(1)

const nextPage = () => {
    if (currentPage.value >= mediaBankEntryOptions.value.length / 6) { return; }
    currentPage.value += 1;
}

const prevPage = () => {
    if (currentPage.value === 1) { return; }
    currentPage.value -= 1;
}

const nextGalleryPage = () => {
    if (currentGalleryPage.value >= mediaBankEntryOptions.value.length / 6) { return; }
    currentGalleryPage.value += 1;
}

const prevGalleryPage = () => {
    if (currentGalleryPage.value === 1) { return; }
    currentGalleryPage.value -= 1;
}

const selectItem = (item) => {
    value.value = item.value;
    showGallery.value = false;
}

const filteredEntries = computed(() => {
    if (searchterm.value === '') {
        return mediaBankEntryOptions.value;
    }

    return mediaBankEntryOptions.value.filter((entry) => entry.label.toLowerCase().includes(searchterm.value.toLowerCase()))
})

const latestEntries = computed(() => {
    return filteredEntries.value.slice(6 * (currentPage.value - 1), 6 * currentPage.value)
});

const galleryEntries = computed(() => {
    return filteredEntries.value.slice(15 * (currentGalleryPage.value - 1), 15 * currentGalleryPage.value)
});

watch(props.content, () =>{
    if (value.value === props.content) { return; }
    value.value = parseInt(this.content, 10);
})


watch(value, () => {
    emit('update:content', value.value);
})

const reload = async (idPresentForReload = null) => {
    showUploader.value = false;
    try {
        const { data } = await axios.get(route('mediaBank.entries.index'));
        const allEntries = data.data || [];

        if (idPresentForReload && !allEntries.some(item => item.id === idPresentForReload)) {
            console.warn(`Entry ${idPresentForReload} not found in library yet.`);
            return false;
        }

        mediaBankEntryOptions.value = allEntries
            .filter(entry => entry.type === props.type)
            .map(entry => ({
                value: entry.id,
                label: entry.title
            }));

        searchterm.value = '';
        showUploader.value = true;
        return true;
    } catch (error) {
        console.error('Failed to reload media bank:', error);
        showUploader.value = true;
        return false;
    }
}

const selectItemIfExists = (id) => {
    const selectableItem = mediaBankEntryOptions.value.find((item) => item.value === id)
    if (!selectableItem) {
        return false
    }

    selectItem(selectableItem);
    return true
}

const onNewMediaBankEntryId = async (id) => {
    waiting.value = true

    const poll = async (attempts = 0) => {
        await reload(id)

        if (selectItemIfExists(id)) {
            await reload()
            waiting.value = false
            return
        }

        if (attempts < 5) {
            setTimeout(() => poll(attempts + 1), 1000 + (attempts * 250))
        } else {
            waiting.value = false
        }
    }

    setTimeout(() => poll(), 1000)
}

onMounted(() => {
    reload()
})
</script>

<template>
<div>
    <div v-if="mediaBankEntryOptions" class="mb-4">
        <div
            class="relative min-h-12"
        >
            <media-upload
                v-if="showUploader"
                @new-media-bank-entry-id="onNewMediaBankEntryId"
            />
            <div v-if="waiting" class="absolute inset-0 flex justify-center items-center bg-white/80">
                <mf-spinner-medium class="w-24 h-12"></mf-spinner-medium>
            </div>
        </div>

        <div class="relative">
            <input-text
                v-model:content="searchterm"
                :label="_mft('mediaBank:searchTheMediaLibrary')"
                :placeholder="_mft('shared:search')"
            ></input-text>
            <span
                v-if="searchterm.length > 0"
                class="absolute top-7 right-2 mt-0.5"
            >
                <button-base
                    size="sm"
                    @click="searchterm = ''"
                ><i class="fa-solid fa-xmark"></i></button-base>
            </span>
        </div>

        <ul class="grid grid-cols-3 gap-2">
            <li
                v-for="item in latestEntries"
                :key="item.value"
                class="relative overflow-hidden group"
            >
                <div
                    v-if="type === 'image'"
                    class="flex aspect-square items-center bg-gray-100 border-2"
                    :class="value === item.value || item.value === props.selectedMediaBankEntryId ? 'border-gray-400' : 'border-transparent'"
                >
                    <media-bank-entries-display
                        :data-url="route('mediaBank.entries.show', item.value)"
                        @click="selectItem(item)"
                        image-size="thumbnail"
                        class="aspect-square cursor-pointer border border-transparent object-contain transition-colors hover:border-gray-400"
                    />

                    <div
                        v-if="value === item.value || item.value === props.selectedMediaBankEntryId"
                        class="bg-gray-400 rounded-bl-xl absolute top-0 right-0 p-1 w-7 h-7 flex justify-center items-center"
                    >
                        <i class="fa-regular fa-circle-check text-white text-lg"></i>
                    </div>

                    <div
                        class="absolute right-0 top-full left-0 bg-gray-400 pb-4 text-center text-xs text-white transition-all group-hover:-mt-5"
                    >
                        {{ item.label }}
                    </div>
                </div>
                <div
                    v-if="type === 'video'"
                    class="flex aspect-video items-center bg-gray-100"
                    @click="selectItem(item)"
                    :title="route('mediaBank.entries.show', item.value)"
                >
                    <div class="pointer-events-none flex h-full w-full items-center">
                        <media-bank-entries-display
                            :data-url="route('mediaBank.entries.show', item.value)"
                            class="aspect-square h-full w-full cursor-pointer border border-transparent object-contain transition-colors hover:border-gray-400"
                            :controls="false"
                        />

                        <div
                            v-if="value === item.value || item.value === props.selectedMediaBankEntryId"
                            class="bg-gray-400 rounded-bl-xl absolute top-0 right-0 p-1 w-7 h-7 flex justify-center items-center"
                        >
                            <i class="fa-regular fa-circle-check text-white text-lg"></i>
                        </div>

                        <div
                            class="absolute right-0 top-full left-0 bg-gray-400 pb-4 text-center text-xs text-white transition-all group-hover:-mt-5"
                        >
                            {{ item.label }}
                        </div>
                    </div>
                </div>
            </li>
        </ul>

        <div class="mt-2 flex items-center justify-between space-x-2">
            <div class="flex items-center space-x-2">
                <button-base
                    size="sm"
                    :style-type="currentPage === 1 ? 'disabled' : 'secondary'"
                    @click="prevPage"
                >
                    <icon-chevron
                        class="h-4 w-4 rotate-90 fill-current transition-all group-hover:text-wlPrimary"
                    />
                </button-base>
                <span class="text-gray-400 text-xs">{{ currentPage > 1 ? currentPage - 1 : '' }}</span>
            </div>
            <div class="flex space-x-2">
                <button-secondary
                    size="sm"
                    type="button"
                    @click.prevent="reload()"
                >
                    <i class="fa-solid fa-arrows-rotate"></i>
                </button-secondary>
                <button-secondary
                    size="sm"
                    @click="showGallery = true"
                >{{ _mft('mediaBank:showGallery') }}</button-secondary>
            </div>
            <div class="flex items-center space-x-2">
                <span class="text-gray-400 text-xs">{{ currentPage + 1 }}</span>
                <button-base
                    size="sm"
                    :style-type="currentPage >= filteredEntries.length / 6 ? 'disabled' : 'secondary'"
                    @click="nextPage"
                >
                    <icon-chevron
                        class="h-4 w-4 -rotate-90 fill-current transition-all"
                    />
                </button-base>
            </div>
        </div>

        <teleport
            v-if="showGallery"
            to="body"
        >
            <div
                class="fixed inset-0 bg-black/20 z-modal-backdrop"
                @click="showGallery = false"
            ></div>
            <div class="fixed inset-4 overflow-y-scroll bg-gray-100 p-4 z-modal">
                <div>
                    <ul class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 pb-12">
                        <li
                            v-for="item in galleryEntries"
                            :key="item.value"
                            class="relative overflow-hidden group"
                        >
                            <div
                                v-if="type === 'image'"
                                class="flex aspect-square items-center bg-gray-200 border-2"
                                :class="value === item.value ? 'border-gray-400' : 'border-transparent'"
                            >
                                <media-bank-entries-display
                                    :data-url="route('mediaBank.entries.show', item.value)"
                                    @click="selectItem(item)"
                                    image-size="thumbnail"
                                    class="aspect-square cursor-pointer border border-transparent object-contain transition-colors hover:border-gray-400"
                                />

                                <div
                                    v-if="value === item.value || item.value === props.selectedMediaBankEntryId"
                                    class="bg-gray-400 rounded-bl-xl absolute top-0 right-0 p-1 w-8 h-8 flex justify-center items-center"
                                >
                                    <i class="fa-regular fa-circle-check text-white text-xl"></i>
                                </div>

                                <div
                                    class="absolute right-0 top-full left-0 bg-gray-400 pb-4 text-center text-xs text-white transition-all group-hover:-mt-5"
                                >
                                    {{ item.label }}
                                </div>
                            </div>
                            <div
                                v-if="type === 'video'"
                                class="flex aspect-video items-center bg-gray-200"
                                @click="selectItem(item)"
                                :title="route('mediaBank.entries.show', item.value)"
                            >
                                <div class="pointer-events-none flex h-full w-full items-center">
                                    <media-bank-entries-display
                                        :data-url="route('mediaBank.entries.show', item.value)"
                                        class="aspect-square h-full w-full cursor-pointer border border-transparent object-contain transition-colors hover:border-gray-400"
                                        :controls="false"
                                    />
                                </div>

                                <div
                                    class="absolute right-0 bottom-full left-0 overflow-hidden overflow-ellipsis whitespace-nowrap bg-gray-400 px-2 pt-4 text-center text-xs text-white transition-all group-hover:-mb-5"
                                >
                                    {{ item.label }}
                                </div>
                            </div>
                        </li>
                    </ul>

                    <div class="fixed bottom-8 left-8 right-8 mt-2 flex justify-center">
                        <div class="flex items-center justify-between space-x-2 bg-white/95 p-4 rounded-wl shadow-lg mb-2 w-full max-w-md">
                            <div class="flex items-center space-x-2">
                                <button-base
                                    :style-type="currentGalleryPage === 1 ? 'disabled' : 'secondary'"
                                    @click="prevGalleryPage"
                                >
                                    <icon-chevron
                                        class="h-4 w-4 rotate-90 fill-current transition-all group-hover:text-wlPrimary"
                                    />
                                </button-base>
                                <span class="text-gray-400 text-xs w-8">{{ currentGalleryPage > 1 ? currentGalleryPage - 1 : '' }}</span>
                            </div>
                            <div class="flex space-x-2">
                                <button-secondary
                                    size="sm"
                                    type="button"
                                    @click.prevent="reload()"
                                >
                                    <i class="fa-solid fa-arrows-rotate"></i>
                                </button-secondary>
                                <button-danger
                                    size="sm"
                                    @click="showGallery = false"
                                >{{ _mft('mediaBank:hideGallery') }}</button-danger>
                            </div>
                            <div class="flex items-center space-x-2">
                                <span class="text-gray-400 text-xs w-8 text-right">{{ currentGalleryPage + 1 }}</span>
                                <button-base
                                    :style-type="currentGalleryPage >= filteredEntries.length / 6 ? 'disabled' : 'secondary'"
                                    @click="nextGalleryPage"
                                >
                                    <icon-chevron
                                        class="h-4 w-4 -rotate-90 fill-current transition-all"
                                    />
                                </button-base>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </teleport>
    </div>
    <mf-spinner-medium v-else class="h-8 w-16"></mf-spinner-medium>
</div>
</template>
